import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Button, ConfirmationModal, showToast, StatusBadge } from '../ui';
import {
  X,
  Copy,
  DollarSign,
  ShieldCheck,
  Clock,
  TrendingDown,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  MapPin,
  Flag,
  RefreshCw,
  Bell,
  Download,
  FileText,
  ExternalLink,
  Ticket,
} from 'lucide-react';
import { getOrderInsight } from '../../data/orderInsightMockData';

type DrawerTabKey = 'summary' | 'items' | 'fulfillment' | 'payments' | 'issues' | 'timeline' | 'actions';

type RiskScore = 'Low' | 'Medium' | 'High';

type InsightData = {
  orderId: string;
  marketplace: string;
  status: string;
  payment: string;
  gross: number;
  netEstimate: number;
  riskScore: RiskScore;
  slaAge: string;
  customer: { name: string; city: string; state: string; country: string };
  marketplaceOrderId: string;
  store: string;
  channel: string;
  workspaceId: string;
  healthChecks: Array<{ status: 'ok' | 'warn' | 'fail'; label: string }>;
  insightChips: string[];
  items: Array<{
    name: string;
    sku: string;
    masterSku?: string | null;
    qty: number;
    price: number;
    tax: number;
    discount: number;
    mappingStatus: string;
    inventoryAvailable: number;
  }>;
  fulfillment: {
    courier: string;
    awb: string;
    steps: Array<{ done: boolean; label: string; time?: string | number | Date | null }>;
    shipBy?: string | number | Date | null;
    deliveryEta?: string | number | Date | null;
    address: string;
    exceptions: Array<{ type: 'error' | 'warn'; message: string }>;
  };
  fees: {
    grossTotal: number;
    shipping: number;
    marketplaceCommission: number;
    paymentGatewayFee: number;
    taxes: number;
    netPayout: number;
  };
  issues: Array<{
    id: string;
    resolved: boolean;
    severity: 'BLOCKER' | 'WARNING' | 'INFO' | (string & {});
    title: string;
    description: string;
    suggestedAction: string;
  }>;
  timeline: Array<{ event: string; time?: string | number | Date | null; source: string }>;
};

type OrderInsightDrawerProps = {
  open: boolean;
  onClose: () => void;
  orderId?: string | null;
};

const fmtDate = (d?: string | number | Date | null) =>
  d
    ? new Date(d).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const fmtCurrency = (v: number) => `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString()}`;

const orderStatusMap: Record<string, string> = {
  Shipped: 'ACTIVE',
  Processing: 'PENDING',
  Delivered: 'OK',
  Returned: 'FAILED',
  Pending: 'PAUSED',
  Confirmed: 'ACTIVE',
};

const riskColors: Record<RiskScore, string> = {
  Low: 'text-emerald-600 dark:text-emerald-400',
  Medium: 'text-amber-600 dark:text-amber-400',
  High: 'text-rose-600 dark:text-rose-400',
};

const riskBg: Record<RiskScore, string> = {
  Low: 'bg-emerald-50 dark:bg-emerald-950',
  Medium: 'bg-amber-50 dark:bg-amber-950',
  High: 'bg-rose-50 dark:bg-rose-950',
};

const tabDefs: Array<{ key: DrawerTabKey; label: string }> = [
  { key: 'summary', label: 'Summary' },
  { key: 'items', label: 'Items' },
  { key: 'fulfillment', label: 'Fulfillment' },
  { key: 'payments', label: 'Payments & Fees' },
  { key: 'issues', label: 'Issues' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'actions', label: 'Actions' },
];

const Skeleton = () => (
  <div data-testid="drawer-loading" className="space-y-4 p-5 animate-pulse">
    <div className="h-6 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
    <div className="grid grid-cols-2 gap-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-20 bg-slate-200 dark:bg-slate-700 rounded-lg" />
      ))}
    </div>
    <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded mt-4" />
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-12 bg-slate-200 dark:bg-slate-700 rounded" />
      ))}
    </div>
  </div>
);

const CopyBtn = ({ text }: { text: string }) => (
  <button
    data-testid={`copy-${text}`}
    onClick={() => {
      navigator.clipboard?.writeText?.(text);
      showToast.info('Copied', text);
    }}
    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
  >
    <Copy className="w-3 h-3" />
  </button>
);

const HealthIcon = ({ status }: { status: 'ok' | 'warn' | 'fail' }) => {
  if (status === 'ok') return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  if (status === 'warn') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <XCircle className="w-4 h-4 text-rose-500" />;
};

const SeverityBadge = ({ severity }: { severity: string }) => {
  const m: Record<string, string> = { BLOCKER: 'FAILED', WARNING: 'WARNING', INFO: 'DISABLED' };
  return (
    <StatusBadge status={m[severity] || 'DISABLED'} showDot>
      {severity}
    </StatusBadge>
  );
};

const SourceBadge = ({ source }: { source: string }) => {
  const c: Record<string, string> = {
    Amazon: 'bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400',
    Flipkart: 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400',
    SmartStore: 'bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-400',
    Delhivery: 'bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-400',
    Ekart: 'bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-400',
    BlueDart: 'bg-cyan-50 dark:bg-cyan-950 text-cyan-700 dark:text-cyan-400',
    Ops: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
  };
  return (
    <span className={cn('px-1.5 py-0.5 text-[10px] font-medium rounded', c[source] || c.Ops)}>
      {source}
    </span>
  );
};

export const OrderInsightDrawer = ({ open, onClose, orderId }: OrderInsightDrawerProps) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<InsightData | null>(null);
  const [tab, setTab] = useState<DrawerTabKey>('summary');
  const [modal, setModal] = useState<string | null>(null);
  const [resolvedIssues, setResolvedIssues] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open && orderId) {
      setLoading(true);
      setTab('summary');
      setResolvedIssues({});
      const timer = window.setTimeout(() => {
        setData(getOrderInsight(orderId) as InsightData);
        setLoading(false);
      }, 600);
      return () => window.clearTimeout(timer);
    }
    return;
  }, [open, orderId]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  const d = data;
  const issueCount = d ? d.issues.filter((i) => !i.resolved && !resolvedIssues[i.id]).length : 0;

  return (
    <>
      <div
        data-testid="drawer-backdrop"
        className="fixed inset-0 bg-black/40 z-40 transition-opacity"
        onClick={handleClose}
      />

      <div
        data-testid="order-insight-drawer"
        className={cn(
          'fixed top-0 right-0 h-full z-50 flex flex-col',
          'bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800',
          'w-full sm:w-[520px] lg:w-[600px] xl:w-[640px]',
          'shadow-2xl',
          'animate-in slide-in-from-right duration-200'
        )}
      >
        <div className="shrink-0 px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Order Insight</h2>
            <button
              data-testid="drawer-close-btn"
              onClick={handleClose}
              className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {d && !loading && (
            <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1 font-mono">
                {d.orderId} <CopyBtn text={d.orderId} />
              </span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span>{d.marketplace}</span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <StatusBadge status={orderStatusMap[d.status] || 'DISABLED'} showDot>
                {d.status}
              </StatusBadge>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span
                className={cn(
                  'font-medium',
                  d.payment === 'COD'
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-slate-600 dark:text-slate-400'
                )}
              >
                {d.payment}
              </span>
            </div>
          )}
        </div>

        {loading ? (
          <Skeleton />
        ) : !d ? (
          <div data-testid="drawer-error" className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <XCircle className="w-10 h-10 text-rose-400 mb-3" />
            <p className="text-sm text-slate-500 dark:text-slate-400">Failed to load order data.</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => {
                if (!orderId) return;
                setLoading(true);
                window.setTimeout(() => {
                  setData(getOrderInsight(orderId) as InsightData);
                  setLoading(false);
                }, 600);
              }}
            >
              <RefreshCw className="w-4 h-4" /> Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="shrink-0 grid grid-cols-4 gap-2 px-5 py-3 border-b border-slate-100 dark:border-slate-800">
              {[
                {
                  label: 'Gross Value',
                  value: fmtCurrency(d.gross),
                  icon: DollarSign,
                  color: 'text-indigo-600 dark:text-indigo-400',
                  bg: 'bg-indigo-50 dark:bg-indigo-950',
                },
                {
                  label: 'Est. Net',
                  value: fmtCurrency(d.netEstimate),
                  icon: TrendingDown,
                  color:
                    d.netEstimate < 0
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-emerald-600 dark:text-emerald-400',
                  bg:
                    d.netEstimate < 0
                      ? 'bg-rose-50 dark:bg-rose-950'
                      : 'bg-emerald-50 dark:bg-emerald-950',
                },
                { label: 'Risk Score', value: d.riskScore, icon: ShieldCheck, color: riskColors[d.riskScore], bg: riskBg[d.riskScore] },
                { label: 'SLA / Age', value: d.slaAge, icon: Clock, color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800' },
              ].map((s, i) => (
                <div key={i} data-testid={`quick-stat-${i}`} className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div className={cn('p-1 rounded w-fit mb-1.5', s.bg)}>
                    <s.icon className={cn('w-3.5 h-3.5', s.color)} />
                  </div>
                  <p className={cn('text-sm font-bold', s.color)}>{s.value}</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 px-5 overflow-x-auto">
              <nav className="flex gap-5 min-w-max">
                {tabDefs.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`insight-tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={cn(
                      'py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
                      tab === t.key
                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                    )}
                  >
                    {t.label}
                    {t.key === 'issues' && issueCount > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-rose-100 dark:bg-rose-900 text-rose-600 dark:text-rose-400">
                        {issueCount}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="p-5 space-y-5">
                {tab === 'summary' && (
                  <>
                    <Section title="Customer">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <Row label="Name" value={d.customer.name} />
                        <Row label="City" value={`${d.customer.city}, ${d.customer.state}`} />
                        <Row label="Country" value={d.customer.country} />
                        <Row
                          label="Payment"
                          value={d.payment}
                          valueClass={d.payment === 'COD' ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}
                        />
                      </div>
                    </Section>

                    <Section title="Marketplace">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <Row
                          label="Marketplace Order"
                          value={
                            <span className="flex items-center gap-1 font-mono text-xs">
                              {d.marketplaceOrderId} <CopyBtn text={d.marketplaceOrderId} />
                            </span>
                          }
                        />
                        <Row label="Store" value={d.store} />
                        <Row label="Channel" value={d.channel} />
                        <Row label="Workspace" value={<span className="font-mono text-xs">{d.workspaceId}</span>} />
                      </div>
                    </Section>

                    <Section title="Health Checks">
                      <div className="grid grid-cols-2 gap-2">
                        {d.healthChecks.map((h, i) => (
                          <div key={i} data-testid={`health-check-${i}`} className="flex items-center gap-2 text-sm">
                            <HealthIcon status={h.status} />
                            <span className="text-slate-700 dark:text-slate-300">{h.label}</span>
                          </div>
                        ))}
                      </div>
                    </Section>

                    {d.insightChips.length > 0 && (
                      <Section title="Insights">
                        <div className="flex flex-wrap gap-2">
                          {d.insightChips.map((chip, i) => (
                            <span
                              key={i}
                              data-testid={`insight-chip-${i}`}
                              className="px-2.5 py-1 text-xs font-medium rounded-full bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                            >
                              {chip}
                            </span>
                          ))}
                        </div>
                      </Section>
                    )}
                  </>
                )}

                {tab === 'items' && (
                  <div className="overflow-x-auto -mx-5">
                    <table data-testid="insight-items-table" className="w-full text-sm min-w-[560px]">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-700">
                          {['Product', 'SKU / Master', 'Qty', 'Price', 'Tax', 'Disc.', 'Mapping', 'Stock'].map((h, i) => (
                            <th
                              key={i}
                              className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {d.items.map((item, i) => (
                          <tr key={i} data-testid={`insight-item-${i}`}>
                            <td className="px-4 py-2.5 text-slate-900 dark:text-slate-100 max-w-[160px] truncate">{item.name}</td>
                            <td className="px-4 py-2.5">
                              <div className="font-mono text-xs text-indigo-600 dark:text-indigo-400">{item.sku}</div>
                              <div className="font-mono text-[10px] text-slate-400">{item.masterSku || '—'}</div>
                            </td>
                            <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{item.qty}</td>
                            <td className="px-4 py-2.5 font-medium text-slate-900 dark:text-slate-100">
                              ${item.price.toLocaleString()}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500">${item.tax}</td>
                            <td className="px-4 py-2.5 text-slate-500">{item.discount > 0 ? `-$${item.discount}` : '—'}</td>
                            <td className="px-4 py-2.5">
                              <StatusBadge status={item.mappingStatus === 'Mapped' ? 'ACTIVE' : 'WARNING'} showDot>
                                {item.mappingStatus}
                              </StatusBadge>
                            </td>
                            <td className="px-4 py-2.5">
                              <span
                                className={cn(
                                  'text-xs font-medium',
                                  item.inventoryAvailable === 0
                                    ? 'text-rose-500'
                                    : item.inventoryAvailable < 20
                                      ? 'text-amber-500'
                                      : 'text-slate-600 dark:text-slate-400'
                                )}
                              >
                                {item.inventoryAvailable}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {tab === 'fulfillment' && (
                  <>
                    <Section title="Shipment Status">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm mb-4">
                        <Row label="Courier" value={d.fulfillment.courier} />
                        <Row
                          label="AWB / Tracking"
                          value={
                            <span className="flex items-center gap-1 font-mono text-xs">
                              {d.fulfillment.awb} <CopyBtn text={d.fulfillment.awb} />
                            </span>
                          }
                        />
                      </div>
                      <div className="flex items-center gap-1 mb-4">
                        {d.fulfillment.steps.map((s, i) => (
                          <Fragment key={i}>
                            <div className={cn('flex flex-col items-center gap-1', i > 0 && 'flex-1')}>
                              {i > 0 && (
                                <div className={cn('h-0.5 w-full rounded', s.done ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700')} />
                              )}
                              <div
                                className={cn(
                                  'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
                                  s.done ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'
                                )}
                              >
                                {s.done ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                              </div>
                              <span className={cn('text-[9px] text-center leading-tight', s.done ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400')}>
                                {s.label}
                              </span>
                              {s.time && <span className="text-[8px] text-slate-400">{fmtDate(s.time)}</span>}
                            </div>
                          </Fragment>
                        ))}
                      </div>
                    </Section>

                    <Section title="SLA Tracking">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <Row label="Ship-by Deadline" value={fmtDate(d.fulfillment.shipBy)} />
                        <Row label="Delivery ETA" value={fmtDate(d.fulfillment.deliveryEta)} />
                      </div>
                    </Section>

                    <Section title="Delivery Address">
                      <div className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                        <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                        {d.fulfillment.address}
                      </div>
                    </Section>

                    {d.fulfillment.exceptions.length > 0 && (
                      <Section title="Fulfillment Exceptions">
                        {d.fulfillment.exceptions.map((ex, i) => (
                          <div
                            key={i}
                            data-testid={`fulfillment-exception-${i}`}
                            className={cn(
                              'flex items-start gap-2 p-2.5 rounded-lg text-sm',
                              ex.type === 'error'
                                ? 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400'
                                : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400'
                            )}
                          >
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            {ex.message}
                          </div>
                        ))}
                      </Section>
                    )}
                  </>
                )}

                {tab === 'payments' && (
                  <>
                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                      <table data-testid="fees-table" className="w-full text-sm">
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {([
                            ['Gross Item Total', d.fees.grossTotal, false],
                            ['Shipping Charges', d.fees.shipping, false],
                            ['Marketplace Commission', -d.fees.marketplaceCommission, true],
                            ['Payment Gateway Fee', -d.fees.paymentGatewayFee, true],
                            ['Taxes (included)', d.fees.taxes, false],
                          ] as const).map(([label, val, isDeduction], i) => (
                            <tr key={i}>
                              <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{label}</td>
                              <td
                                className={cn(
                                  'px-4 py-2.5 text-right font-medium',
                                  isDeduction ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-slate-100'
                                )}
                              >
                                {isDeduction ? `-$${Math.abs(val).toLocaleString()}` : `$${val.toLocaleString()}`}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-slate-50 dark:bg-slate-800/50">
                            <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">Net Payout Estimate</td>
                            <td
                              className={cn(
                                'px-4 py-3 text-right font-bold text-lg',
                                d.fees.netPayout < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'
                              )}
                            >
                              {fmtCurrency(d.fees.netPayout)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div
                      data-testid="payout-disclaimer"
                      className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-xs text-slate-500 dark:text-slate-400"
                    >
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                      Payout values are estimates until settlement report arrives.
                    </div>
                  </>
                )}

                {tab === 'issues' && (
                  <>
                    {d.issues.length === 0 ? (
                      <div data-testid="no-issues" className="flex flex-col items-center justify-center py-10 text-center">
                        <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" />
                        <p className="text-sm text-slate-500 dark:text-slate-400">No issues found for this order.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {d.issues.map((iss) => {
                          const isResolved = iss.resolved || resolvedIssues[iss.id];
                          return (
                            <div
                              key={iss.id}
                              data-testid={`issue-card-${iss.id}`}
                              className={cn(
                                'p-3 rounded-lg border',
                                isResolved
                                  ? 'opacity-60 bg-slate-50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-700'
                                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                              )}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <SeverityBadge severity={iss.severity} />
                                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{iss.title}</span>
                                </div>
                                {isResolved && (
                                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">Resolved</span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{iss.description}</p>
                              <p className="text-xs text-slate-600 dark:text-slate-300 mb-2.5 italic">Suggested: {iss.suggestedAction}</p>
                              {!isResolved && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    data-testid={`resolve-${iss.id}`}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setResolvedIssues((p) => ({ ...p, [iss.id]: true }));
                                      showToast.success('Resolved', iss.title);
                                    }}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark as Resolved
                                  </Button>
                                  <Button
                                    data-testid={`ticket-${iss.id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => showToast.info('Ticket Created', `Ticket for "${iss.title}" created (mock).`)}
                                  >
                                    <Ticket className="w-3.5 h-3.5" /> Create Ticket
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {tab === 'timeline' && (
                  <div className="relative pl-6">
                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-slate-200 dark:bg-slate-700" />
                    <div className="space-y-5">
                      {d.timeline.map((ev, i) => (
                        <div key={i} data-testid={`timeline-event-${i}`} className="relative flex items-start gap-3">
                          <div
                            className={cn(
                              'absolute -left-6 w-6 h-6 rounded-full flex items-center justify-center text-white',
                              i === d.timeline.length - 1 ? (ev.event.includes('Return') ? 'bg-rose-500' : 'bg-indigo-500') : 'bg-emerald-500'
                            )}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                          </div>
                          <div className="flex-1 ml-3">
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{ev.event}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-slate-400 dark:text-slate-500">{fmtDate(ev.time)}</span>
                              <SourceBadge source={ev.source} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tab === 'actions' && (
                  <>
                    <Section title="Primary Actions">
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          data-testid="action-open-full"
                          variant="outline"
                          className="justify-start"
                          onClick={() => showToast.info('Open Details', 'Full order details page would open here.')}
                        >
                          <ExternalLink className="w-4 h-4" /> Open Full Order Details
                        </Button>
                        <Button data-testid="action-resync" variant="outline" className="justify-start" onClick={() => setModal('Resync Order')}>
                          <RefreshCw className="w-4 h-4" /> Resync Order
                        </Button>
                        <Button
                          data-testid="action-resend-notif"
                          variant="outline"
                          className="justify-start"
                          onClick={() => setModal('Resend Notification')}
                        >
                          <Bell className="w-4 h-4" /> Resend Notification
                        </Button>
                        <Button
                          data-testid="action-flag"
                          variant="outline"
                          className="justify-start text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-950"
                          onClick={() => setModal('Flag for Review')}
                        >
                          <Flag className="w-4 h-4" /> Flag for Review
                        </Button>
                      </div>
                    </Section>
                    <Section title="Secondary Actions">
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          data-testid="action-download-invoice"
                          variant="ghost"
                          className="justify-start"
                          onClick={() => showToast.info('Download', 'Invoice download started (mock).')}
                        >
                          <FileText className="w-4 h-4" /> Download Invoice
                        </Button>
                        <Button
                          data-testid="action-download-label"
                          variant="ghost"
                          className="justify-start"
                          onClick={() => showToast.info('Download', 'Shipping label download started (mock).')}
                        >
                          <Download className="w-4 h-4" /> Download Label
                        </Button>
                      </div>
                    </Section>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmationModal
        open={!!modal}
        onOpenChange={(nextOpen: boolean) => {
          if (!nextOpen) setModal(null);
        }}
        title={modal || ''}
        description={`Are you sure you want to ${modal?.toLowerCase()} for ${orderId}? This action will be logged.`}
        confirmLabel={modal || 'Confirm'}
        variant={modal === 'Flag for Review' ? 'danger' : 'primary'}
        onConfirm={() => {
          showToast.success(`${modal} confirmed`, `Action applied to ${orderId}.`);
          setModal(null);
        }}
        onCancel={() => setModal(null)}
      />
    </>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div>
    <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2.5">{title}</h4>
    {children}
  </div>
);

const Row = ({ label, value, valueClass }: { label: string; value: ReactNode; valueClass?: string }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className={cn('text-slate-900 dark:text-slate-100', valueClass)}>{value}</span>
  </div>
);

const Fragment = ({ children }: { children: ReactNode }) => <>{children}</>;
